<script setup lang="ts">
import { ref } from 'vue'
import { Copy, CheckCircle2, CircleHelp, XCircle, Clock, User, Building2, ChevronDown, ChevronRight, Wallet, CreditCard, Building, AlertCircle, RefreshCw } from 'lucide-vue-next'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { CombinedCustomer, VirtualAccount, ExternalAccount, BridgeWallet } from '@/types/bridge'
import { API, HOST } from '@/utils/http'
import ConnectExternalAccountForm from './ConnectExternalAccountForm.vue'
import FundAccountDialog from './FundAccountDialog.vue'

const props = defineProps<{
  customers: CombinedCustomer[]
  loading: boolean
}>()

// Track expanded rows
const expandedRows = ref<Set<string>>(new Set())

// Track which customer is showing the connect form
const showingConnectForm = ref<string | null>(null)

// Track account data for each customer
interface CustomerAccountData {
  virtualAccounts: VirtualAccount[] | null
  externalAccounts: ExternalAccount[] | null
  wallets: BridgeWallet[] | null
  loading: boolean
  error: string | null
}

const customerAccountsData = ref<Map<string, CustomerAccountData>>(new Map())

const toggleRow = async (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`
  if (expandedRows.value.has(key)) {
    expandedRows.value.delete(key)
  } else {
    expandedRows.value.add(key)
    // Fetch account data if not already loaded
    if (!customerAccountsData.value.has(key)) {
      await fetchCustomerAccounts(customer)
    }
  }
}

const isExpanded = (customer: CombinedCustomer) => {
  return expandedRows.value.has(`${customer.type}-${customer.id}`)
}

const fetchCustomerAccounts = async (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`

  // Initialize loading state
  customerAccountsData.value.set(key, {
    virtualAccounts: null,
    externalAccounts: null,
    wallets: null,
    loading: true,
    error: null
  })

  try {
    // Fetch all three account types in parallel
    const [virtualAccountsRes, externalAccountsRes, walletsRes] = await Promise.all([
      API.get(`${HOST}/payments/bridge/customers/${customer.id}/virtual-accounts`).catch(() => ({ data: [] })),
      API.get(`${HOST}/payments/bridge/customers/${customer.id}/external-accounts`).catch(() => ({ data: [] })),
      API.get(`${HOST}/payments/bridge/customers/${customer.id}/wallets`).catch(() => ({ data: { wallets: [] } }))
    ])

    // Filter out null/undefined items and ensure we have arrays
    const virtualAccounts = Array.isArray(virtualAccountsRes.data)
      ? virtualAccountsRes.data.filter(item => item && item.id)
      : []
    const externalAccounts = Array.isArray(externalAccountsRes.data)
      ? externalAccountsRes.data.filter(item => item && item.id)
      : []
    const wallets = Array.isArray(walletsRes.data?.wallets)
      ? walletsRes.data.wallets.filter((item: any) => item && item.id)
      : []

    customerAccountsData.value.set(key, {
      virtualAccounts,
      externalAccounts,
      wallets,
      loading: false,
      error: null
    })
  } catch (error: any) {
    customerAccountsData.value.set(key, {
      virtualAccounts: null,
      externalAccounts: null,
      wallets: null,
      loading: false,
      error: error.response?.data?.detail || 'Failed to load account data'
    })
  }
}

const getAccountData = (customer: CombinedCustomer): CustomerAccountData | null => {
  const key = `${customer.type}-${customer.id}`
  return customerAccountsData.value.get(key) || null
}

const showConnectForm = (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`
  showingConnectForm.value = key
}

const hideConnectForm = () => {
  showingConnectForm.value = null
}

const isShowingConnectForm = (customer: CombinedCustomer) => {
  const key = `${customer.type}-${customer.id}`
  return showingConnectForm.value === key
}

const handleConnectSuccess = async (customer: CombinedCustomer) => {
  // Hide the form
  hideConnectForm()

  // Refresh the account data for this customer
  await fetchCustomerAccounts(customer)
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
}

const emit = defineEmits<{
  (e: 'customer-updated'): void
}>()

const syncingCustomers = ref<Set<string>>(new Set())

const isSyncing = (customer: CombinedCustomer) => {
  return syncingCustomers.value.has(`${customer.type}-${customer.id}`)
}

const syncCustomerStatus = async (customer: CombinedCustomer, event: Event) => {
  event.stopPropagation()
  const key = `${customer.type}-${customer.id}`
  syncingCustomers.value.add(key)
  try {
    await API.post(`${HOST}/payments/bridge/customers/${customer.id}/sync`)
    emit('customer-updated')
  } catch (err: any) {
    console.error('Failed to sync customer status:', err)
  } finally {
    syncingCustomers.value.delete(key)
  }
}

const getOverallStatus = (customer: CombinedCustomer): 'approved' | 'rejected' | 'awaiting_ubo' | 'in_progress' => {
  const tosApproved = customer.tos_status === 'approved'
  const kycApproved = customer.kyc_status === 'approved'
  const kycRejected = ['rejected', 'paused', 'offboarded'].includes(customer.kyc_status)
  const awaitingUbo = customer.kyc_status === 'awaiting_ubo'

  if (tosApproved && kycApproved) return 'approved'
  if (kycRejected) return 'rejected'
  if (awaitingUbo) return 'awaiting_ubo'
  return 'in_progress'
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<template>
  <div class="h-full">
    <!-- Loading state -->
    <div v-if="loading" class="p-4">
      <div class="space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center space-x-3 animate-pulse">
          <div class="h-10 w-10 bg-gray-200 rounded-full"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="customers.length === 0" class="border rounded-md p-8 text-center text-gray-500">
      No Bridge customers yet. Create one using the form above.
    </div>

    <!-- Combined table -->
    <div v-else class="border rounded-md">
      <table class="w-full">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="h-12 w-10 px-2 text-left align-middle font-medium text-muted-foreground"></th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Type</th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
            <th class="h-12 px-4 text-center align-middle font-medium text-muted-foreground">Status</th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Created</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="customer in customers" :key="`${customer.type}-${customer.id}`">
            <!-- Main row -->
            <tr
              class="border-b hover:bg-muted/50 cursor-pointer"
              @click="toggleRow(customer)"
            >
              <!-- Expand toggle -->
              <td class="p-2 align-middle text-center">
                <ChevronDown v-if="isExpanded(customer)" class="h-4 w-4 text-muted-foreground" />
                <ChevronRight v-else class="h-4 w-4 text-muted-foreground" />
              </td>

              <!-- Type Badge -->
              <td class="p-4 align-middle">
                <Badge
                  v-if="customer.type === 'individual'"
                  class="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100"
                >
                  <User class="h-3 w-3 mr-1" />
                  Individual
                </Badge>
                <Badge
                  v-else
                  class="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100"
                >
                  <Building2 class="h-3 w-3 mr-1" />
                  Business
                </Badge>
              </td>

              <!-- Name -->
              <td class="p-4 align-middle">
                <div>
                  <div class="font-medium">{{ customer.name }}</div>
                  <!-- Show contact info for business customers -->
                  <div v-if="customer.type === 'business' && customer.contactName" class="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <User class="h-3 w-3" />
                    <span>{{ customer.contactName }}</span>
                    <span v-if="customer.contactEmail" class="text-gray-400">{{ customer.contactEmail }}</span>
                  </div>
                </div>
              </td>

              <!-- Status -->
              <td class="p-4 align-middle text-center">
                <div class="inline-flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <span class="inline-flex cursor-pointer">
                          <CheckCircle2 v-if="getOverallStatus(customer) === 'approved'" class="h-5 w-5 text-green-500" />
                          <XCircle v-else-if="getOverallStatus(customer) === 'rejected'" class="h-5 w-5 text-red-500" />
                          <Clock v-else-if="getOverallStatus(customer) === 'awaiting_ubo'" class="h-5 w-5 text-amber-500" />
                          <CircleHelp v-else class="h-5 w-5 text-gray-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div class="text-sm space-y-1">
                          <div>ToS: {{ customer.tos_status || '-' }}</div>
                          <div>{{ customer.type === 'business' ? 'KYB' : 'KYC' }}: {{ customer.kyc_status || '-' }}</div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <button
                    v-if="getOverallStatus(customer) !== 'approved' && customer.bridge_customer_id"
                    @click="syncCustomerStatus(customer, $event)"
                    class="text-muted-foreground hover:text-foreground"
                    :disabled="isSyncing(customer)"
                    title="Refresh status from Bridge"
                  >
                    <RefreshCw :class="['h-3.5 w-3.5', isSyncing(customer) && 'animate-spin']" />
                  </button>
                </div>
              </td>

              <!-- Created -->
              <td class="p-4 align-middle text-sm text-muted-foreground">
                {{ formatDate(customer.created_at) }}
              </td>
            </tr>

            <!-- Expanded details row -->
            <tr v-if="isExpanded(customer)" class="bg-muted/30">
              <td colspan="5" class="p-4">
                <div class="pl-6 space-y-4 text-sm">
                  <!-- Customer Info Section -->
                  <div class="grid grid-cols-2 gap-4 max-w-2xl pb-4 border-b">
                    <!-- Bridge ID -->
                    <div>
                      <span class="text-muted-foreground">Bridge ID:</span>
                      <div class="mt-1">
                        <button
                          v-if="customer.bridge_customer_id"
                          @click.stop="copyToClipboard(customer.bridge_customer_id!)"
                          class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 text-xs font-mono"
                        >
                          <span>{{ customer.bridge_customer_id }}</span>
                          <Copy class="h-3 w-3 text-muted-foreground" />
                        </button>
                        <span v-else class="text-xs text-gray-400 italic">Not assigned</span>
                      </div>
                    </div>

                    <!-- Internal ID -->
                    <div>
                      <span class="text-muted-foreground">Internal ID:</span>
                      <div class="mt-1 text-xs font-mono">{{ customer.id }}</div>
                    </div>

                    <!-- ToS Status -->
                    <div>
                      <span class="text-muted-foreground">ToS Status:</span>
                      <div class="mt-1">
                        <a
                          v-if="customer.link?.tos_url"
                          :href="customer.link.tos_url"
                          target="_blank"
                          rel="noopener noreferrer"
                          @click.stop
                          class="inline-block"
                        >
                          <Badge
                            :class="customer.tos_status === 'approved'
                              ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'"
                            class="cursor-pointer transition-colors"
                          >
                            {{ customer.tos_status || 'pending' }}
                          </Badge>
                        </a>
                        <Badge
                          v-else
                          :class="customer.tos_status === 'approved'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200'"
                        >
                          {{ customer.tos_status || 'pending' }}
                        </Badge>
                      </div>
                    </div>

                    <!-- KYC/KYB Status -->
                    <div>
                      <span class="text-muted-foreground">{{ customer.type === 'business' ? 'KYB' : 'KYC' }} Status:</span>
                      <div class="mt-1">
                        <a
                          v-if="customer.link?.kyc_url"
                          :href="customer.link.kyc_url"
                          target="_blank"
                          rel="noopener noreferrer"
                          @click.stop
                          class="inline-block"
                        >
                          <Badge
                            :class="{
                              'bg-green-100 text-green-700 border-green-200 hover:bg-green-200': customer.kyc_status === 'approved',
                              'bg-red-100 text-red-700 border-red-200 hover:bg-red-200': ['rejected', 'paused', 'offboarded'].includes(customer.kyc_status),
                              'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200': customer.kyc_status === 'awaiting_ubo',
                              'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200': !['approved', 'rejected', 'paused', 'offboarded', 'awaiting_ubo'].includes(customer.kyc_status)
                            }"
                            class="cursor-pointer transition-colors"
                          >
                            {{ customer.kyc_status || 'not_started' }}
                          </Badge>
                        </a>
                        <Badge
                          v-else
                          :class="{
                            'bg-green-100 text-green-700 border-green-200': customer.kyc_status === 'approved',
                            'bg-red-100 text-red-700 border-red-200': ['rejected', 'paused', 'offboarded'].includes(customer.kyc_status),
                            'bg-amber-100 text-amber-700 border-amber-200': customer.kyc_status === 'awaiting_ubo',
                            'bg-gray-100 text-gray-700 border-gray-200': !['approved', 'rejected', 'paused', 'offboarded', 'awaiting_ubo'].includes(customer.kyc_status)
                          }"
                        >
                          {{ customer.kyc_status || 'not_started' }}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <!-- Accounts Section -->
                  <div v-if="getAccountData(customer)">
                    <!-- Loading State -->
                    <div v-if="getAccountData(customer)!.loading" class="flex items-center gap-2 text-muted-foreground py-4">
                      <div class="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                      <span>Loading account data...</span>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="getAccountData(customer)!.error" class="flex items-center gap-2 text-red-600 py-4">
                      <AlertCircle class="h-4 w-4" />
                      <span>{{ getAccountData(customer)!.error }}</span>
                    </div>

                    <!-- Account Data -->
                    <div v-else class="space-y-4">
                      <!-- Virtual Accounts Section -->
                      <div>
                        <div class="flex items-center gap-2 mb-2 font-medium">
                          <Building class="h-4 w-4 text-blue-600" />
                          <span>Virtual Accounts (Fiat → Crypto)</span>
                          <Badge variant="secondary" class="ml-2">{{ getAccountData(customer)!.virtualAccounts?.length || 0 }}</Badge>
                        </div>
                        <div v-if="!getAccountData(customer)!.virtualAccounts?.length" class="text-xs text-muted-foreground ml-6">
                          No virtual accounts
                        </div>
                        <div v-else class="space-y-2 ml-6">
                          <div
                            v-for="va in getAccountData(customer)!.virtualAccounts"
                            :key="va.id"
                            class="bg-white border rounded p-3 space-y-2"
                          >
                            <div class="flex items-center justify-between">
                              <span class="text-xs font-mono text-muted-foreground">{{ va.id }}</span>
                              <Badge :class="va.status === 'activated' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'">
                                {{ va.status }}
                              </Badge>
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span class="text-muted-foreground">Bank:</span>
                                <div class="font-medium">{{ va.source_deposit_instructions?.bank_name || 'N/A' }}</div>
                              </div>
                              <div>
                                <span class="text-muted-foreground">Destination:</span>
                                <div class="font-medium">{{ va.destination?.currency?.toUpperCase() || 'N/A' }} ({{ va.destination?.payment_rail || 'N/A' }})</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- External Accounts Section -->
                      <div>
                        <div class="flex items-center gap-2 mb-2 font-medium">
                          <CreditCard class="h-4 w-4 text-purple-600" />
                          <span>External Bank Accounts</span>
                          <Badge variant="secondary" class="ml-2">{{ getAccountData(customer)!.externalAccounts?.length || 0 }}</Badge>
                        </div>
                        <div v-if="!getAccountData(customer)!.externalAccounts?.length" class="ml-6">
                          <div v-if="!isShowingConnectForm(customer)">
                            <div v-if="customer.kyc_status === 'approved'" class="flex items-center gap-2">
                              <span class="text-xs text-muted-foreground">No external accounts</span>
                              <Button
                                variant="link"
                                size="sm"
                                @click.stop="showConnectForm(customer)"
                                class="h-auto p-0 text-xs"
                              >
                                Connect
                              </Button>
                            </div>
                            <div v-else class="text-xs text-muted-foreground">
                              No external accounts ({{ customer.type === 'business' ? 'KYB' : 'KYC' }} must be approved first)
                            </div>
                          </div>
                          <ConnectExternalAccountForm
                            v-else
                            :customer-id="customer.id"
                            @success="handleConnectSuccess(customer)"
                            @cancel="hideConnectForm"
                          />
                        </div>
                        <div v-else class="space-y-2 ml-6">
                          <div
                            v-for="ea in getAccountData(customer)!.externalAccounts"
                            :key="ea.id"
                            class="bg-white border rounded p-3 space-y-2"
                          >
                            <div class="flex items-center justify-between">
                              <span class="text-xs font-mono text-muted-foreground">{{ ea.id }}</span>
                              <div class="flex items-center gap-2">
                                <FundAccountDialog
                                  v-if="ea.active && customer.kyc_status === 'approved'"
                                  :customer="customer"
                                  :external-account="ea"
                                  @funded="fetchCustomerAccounts(customer)"
                                />
                                <Badge v-if="ea.beneficiary_address_valid" class="bg-green-100 text-green-700">Validated</Badge>
                                <Badge :class="ea.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'">
                                  {{ ea.active ? 'Active' : 'Inactive' }}
                                </Badge>
                              </div>
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span class="text-muted-foreground">Bank:</span>
                                <div class="font-medium">{{ ea.bank_name || 'N/A' }}</div>
                              </div>
                              <div>
                                <span class="text-muted-foreground">Owner:</span>
                                <div class="font-medium">{{ ea.account_owner_name || 'N/A' }}</div>
                              </div>
                              <div>
                                <span class="text-muted-foreground">Type:</span>
                                <div class="font-medium">{{ ea.account_type?.toUpperCase() || 'N/A' }}</div>
                              </div>
                              <div>
                                <span class="text-muted-foreground">Currency:</span>
                                <div class="font-medium">{{ ea.currency?.toUpperCase() || 'N/A' }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Wallets Section -->
                      <div>
                        <div class="flex items-center gap-2 mb-2 font-medium">
                          <Wallet class="h-4 w-4 text-amber-600" />
                          <span>Bridge Wallets</span>
                          <Badge variant="secondary" class="ml-2">{{ getAccountData(customer)!.wallets?.length || 0 }}</Badge>
                        </div>
                        <div v-if="!getAccountData(customer)!.wallets?.length" class="text-xs text-muted-foreground ml-6">
                          No wallets
                        </div>
                        <div v-else class="space-y-2 ml-6">
                          <div
                            v-for="wallet in getAccountData(customer)!.wallets"
                            :key="wallet.id"
                            class="bg-white border rounded p-3 space-y-2"
                          >
                            <div class="flex items-center justify-between">
                              <span class="text-xs font-mono text-muted-foreground">{{ wallet.id }}</span>
                              <Badge class="bg-amber-100 text-amber-700">{{ wallet.chain }}</Badge>
                            </div>
                            <div class="text-xs">
                              <span class="text-muted-foreground">Address:</span>
                              <button
                                @click.stop="copyToClipboard(wallet.address)"
                                class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 font-mono mt-1"
                              >
                                <span class="break-all">{{ wallet.address }}</span>
                                <Copy class="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
